
/* @flow */
import React, {PropTypes } from 'react';
import Translate from 'react-translate-component';
import { proxyCounterpart } from '../../../scripts/proxyCounterpart';
import { InputField, GeneralError, SpinnerButton, CountryPicker, PayTermsPicker } from 'components';
import { Utils, CustomerService } from 'frilans-finans';

const DEFAULT_COUNTRY_FOR_NEW_CUSTOMERS = 'SWEDEN';

export class EditCustomer extends React.Component {

  static propTypes = {
    edit: PropTypes.bool.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    customer: PropTypes.object,
    callback: PropTypes.func.isRequired
  };

  static contextTypes = {
    user: PropTypes.object.isRequired
  };

  constructor (props, context) {
    super(props, context);

    this.state = {
      customer: this.prepareCustomer(),
      showErrorWithoutBlur: false,
      posting: false,
      postError: false,
      errorMessage: null
    };

    this.newValueFromChild = this.newValueFromChild.bind(this);
    this.setCountry = this.setCountry.bind(this);
    this.setPayTerms = this.setPayTerms.bind(this);
    this.clickSubmit = this.clickSubmit.bind(this);
    this.handlePostSuccess = this.handlePostSuccess.bind(this);
    this.handlePostError = this.handlePostError.bind(this);
  }

  prepareCustomer () {
    let customer;

    if (this.props.edit) {
      customer = {
        id: this.props.customer.id,
        name: this.props.customer.name,
        vatNr: this.props.customer.vatNr,
        co: this.props.customer.address.co,
        street: this.props.customer.address.street,
        zip: this.props.customer.address.zip,
        city: this.props.customer.address.city,
        country: this.props.customer.address.country,
        contact: this.props.customer.contact,
        sendToEmail: this.props.customer.sendToEmail,
        sendToEmailSecondary: this.props.customer.sendToEmailSecondary,
        phone: this.props.customer.phone,
        payTerms: this.props.customer.payTerms
      };
    } else {
      customer = {
        name: '',
        vatNr: '',
        co: '',
        street: '',
        zip: '',
        city: '',
        country: DEFAULT_COUNTRY_FOR_NEW_CUSTOMERS,
        contact: '',
        sendToEmail: '',
        sendToEmailSecondary: '',
        phone: '',
        payTerms: 30
      };
    }

    return Utils.getInputValueObjects(customer);
  }

  newValueFromChild (name, values) {
    var tempFormValues = this.state.customer;

    Object.assign(tempFormValues[name], values);
    this.setState({customer: tempFormValues});
  }

  setCountry (country) {
    this.newValueFromChild('country', {value: country, valid: true});
  }

  setPayTerms (payTerms) {
    this.newValueFromChild('payTerms', {value: payTerms, valid: true});
  }

  submitForm () {
    var params = Utils.getPostParams(this.state.customer);
    params.userId = this.context.user.id;

    this.setState({
      posting: true,
      postError: false
    });

    if (this.props.edit) {
      CustomerService.updateBillingEmails(params.id, params)
        .then(() => {
          CustomerService.update(params.id, params)
            .then((customer) => {
              return customer;
            })
            .then(this.handlePostSuccess)
            .catch(this.handlePostError);
        });
    } else {
      CustomerService.post(params)
        .then((customer) => {
          CustomerService.updateBillingEmails(customer.id, params);
          // We append secondary email to the customer response as we dont have it after posting
          customer.sendToEmailSecondary = params.sendToEmailSecondary || '';
          return customer;
        })
        .then(this.handlePostSuccess)
        .catch(this.handlePostError);
    }
  }

  handlePostSuccess (customer) {
    this.props.callback(customer);
  }

  handlePostError (error) {
    let errors = CustomerService.getError(error);

    this.setState({
      posting: false,
      postError: true,
      errorMessage: errors
    });
  }

  clickSubmit (e) {
    e.preventDefault();

    this.setState({showErrorWithoutBlur: true}, () => {
      if (Utils.validateInputValues(this.state.customer)) {
        this.submitForm();
      }
    });
  }

  renderHeading () {
    if (this.props.edit) {
      return (<h2><Translate content='EDIT_INFO'/></h2>);
    }

    return (<h2><Translate content='CUSTOMER_ADD'/></h2>);
  }

  renderCountryError () {
    if (this.state.countryError) {
      return (<p className="error"><Translate content='COUNTRY_ERROR' /></p>);
    }
  }

  renderPostError () {
    if (this.state.postError) {
      if (!this.state.errorMessage) {
        return (<GeneralError />);
      } else {
        return (
          <div>
            {this.state.errorMessage.map((error, i) => {
              return (
                <p className='error' key={i}>{error}</p>
              );
            })}
          </div>
        );
      }
    }
  }

  renderNewCustomerInfo () {
    if (this.props.edit) {
      return;
    }
    return (<p><Translate content='NEW_CUSTOMER_INFO'/></p>);
  }

  render () {
    let disableVatInput = (this.props.edit && this.props.customer.vatNr.length > 0) ? 'true' : 'false';

    return (
      <div className='edit-customer'>
        {this.renderHeading()}
        {this.renderNewCustomerInfo()}
        <form noValidate onSubmit={this.clickSubmit}>
          <InputField type='text' name='name' value={this.state.customer.name.value}
            placeholder={proxyCounterpart('NAME')} callback={this.newValueFromChild}
            required='true' minLength='3' maxLength='50' errorMessage={proxyCounterpart('NAME_ERROR')}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} />

          <InputField type='text' name='vatNr' value={this.state.customer.vatNr.value}
            placeholder={proxyCounterpart('ORG_NR')} callback={this.newValueFromChild}
            required='false' errorMessage={proxyCounterpart('ORG_NR_ERROR')}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} disabled={disableVatInput} />

          <InputField type='text' name='contact' value={this.state.customer.contact.value}
            placeholder={proxyCounterpart('REF')} callback={this.newValueFromChild}
            required='true' maxLength='30' minLength='0' errorMessage={proxyCounterpart('REF_ERROR')}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} />

          <InputField type='text' name='phone' value={this.state.customer.phone.value}
            placeholder={proxyCounterpart('TELEPHONE_NR')} callback={this.newValueFromChild}
            required='true' minLength='0' errorMessage={proxyCounterpart('TELEPHONE_ERROR')}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} />

          <InputField type='text' name='sendToEmail' value={this.state.customer.sendToEmail.value}
            placeholder={proxyCounterpart('EMAIL_BILLING')} callback={this.newValueFromChild}
            required='true' minLength='0' errorMessage={proxyCounterpart('EMAIL_ERROR')}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} />

          <InputField type='text' name='sendToEmailSecondary' value={this.state.customer.sendToEmailSecondary.value}
            placeholder={proxyCounterpart('EMAIL_BILLING_SECONDARY')} callback={this.newValueFromChild}
            required='false' minLength='0' errorMessage={proxyCounterpart('EMAIL_ERROR')}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} />

          <InputField type='text' name='co' value={this.state.customer.co.value}
            placeholder={proxyCounterpart('CO')} callback={this.newValueFromChild}
            required='false' errorMessage={proxyCounterpart('CO_ERROR')}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} />

          <InputField type='text' name='street' value={this.state.customer.street.value}
            placeholder={proxyCounterpart('ADDRESS')} callback={this.newValueFromChild}
            required='true' minLength='3' maxLength='50' errorMessage={proxyCounterpart('ADDRESS_ERROR')}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} />

          <InputField type='text' name='zip' value={this.state.customer.zip.value}
            placeholder={proxyCounterpart('POSTAL_CODE')} callback={this.newValueFromChild}
            required='true' minLength='3' errorMessage={proxyCounterpart('POSTAL_CODE_ERROR')}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} />

          <InputField type='text' name='city' value={this.state.customer.city.value}
            placeholder={proxyCounterpart('CITY')} callback={this.newValueFromChild}
            required='true' minLength='2' maxLength='40' errorMessage={proxyCounterpart('CITY_ERROR')}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} />
          <CountryPicker
            setCountry={this.setCountry}
            countryName={this.state.customer.country.value}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur}
          />

          <PayTermsPicker
            setPayTerms={this.setPayTerms}
            payTerms={this.state.customer.payTerms.value}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur}
          />

          {this.renderPostError()}
          {this.renderCountryError()}

          <div className='edit-customer-buttons'>
            <SpinnerButton text={proxyCounterpart('SAVE')}
              loading={this.state.posting} classes='btn'/>
            <a className='btn outlined blue' onClick={this.props.onCloseModal}>
              <span><Translate content='CLOSE'/></span>
            </a>
          </div>
        </form>
      </div>
    );
  }
}

export default EditCustomer;



/** WEBPACK FOOTER **
 ** ./src/views/invoice-tool/customer/edit-customer.js
 **/